import { Users, UserCheck, FileText, Clock, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react'

interface KPIData {
  current: Record<string, number>
  previous: Record<string, number>
  deltas: Record<string, number>
}

interface AnalyticsKPIsProps {
  kpis: KPIData
}

interface KPIConfig {
  label: string
  key: string
  format: (value: number) => string
  icon: LucideIcon
  invertDelta?: boolean
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  return `${mins}m${secs.toString().padStart(2, '0')}s`
}

const kpiConfigs: KPIConfig[] = [
  {
    label: 'Visites',
    key: 'sessions',
    format: (v) => v.toLocaleString(),
    icon: Users,
  },
  {
    label: 'Visiteurs uniques',
    key: 'totalUsers',
    format: (v) => v.toLocaleString(),
    icon: UserCheck,
  },
  {
    label: 'Pages / Visite',
    key: 'screenPageViewsPerSession',
    format: (v) => v.toFixed(1),
    icon: FileText,
  },
  {
    label: 'Durée moyenne',
    key: 'averageSessionDuration',
    format: (v) => formatDuration(v),
    icon: Clock,
  },
  {
    label: 'Taux de rebond',
    key: 'bounceRate',
    format: (v) => `${(v * 100).toFixed(1)}%`,
    icon: TrendingDown,
    invertDelta: true,
  },
  {
    label: 'Sessions engagées',
    key: 'engagedSessions',
    format: (v) => v.toLocaleString(),
    icon: TrendingUp,
  },
]

export function AnalyticsKPIs({ kpis }: AnalyticsKPIsProps) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
      {kpiConfigs.map((config) => {
        const value = kpis.current[config.key] ?? 0
        const delta = kpis.deltas[config.key] ?? 0

        const isPositiveChange = delta > 0
        const isImprovement = config.invertDelta ? !isPositiveChange : isPositiveChange
        const deltaColor = delta === 0
          ? 'text-[#8B8FA7]'
          : isImprovement
            ? 'text-emerald-500'
            : 'text-red-500'
        const arrow = delta > 0 ? '\u2191' : delta < 0 ? '\u2193' : ''
        const absDelta = Math.abs(delta)
        const deltaText = absDelta >= 1000 ? `${(absDelta / 1000).toFixed(1)}x` : `${absDelta.toFixed(1)}%`
        const deltaFormatted = `${delta > 0 ? '+' : '-'}${deltaText}`

        const Icon = config.icon

        return (
          <div
            key={config.key}
            className="bg-white border border-[#E2E4ED] rounded-xl p-4"
          >
            <div className="flex items-center justify-between">
              <div className="w-9 h-9 rounded-lg bg-[#6366F1]/10 flex items-center justify-center">
                <Icon className="w-4 h-4 text-[#6366F1]" strokeWidth={1.25} />
              </div>
              {delta !== 0 && (
                <span className={`text-xs font-mono font-semibold ${deltaColor}`}>
                  {arrow} {deltaFormatted}
                </span>
              )}
            </div>
            <div className="mt-3">
              <div className="text-2xl font-mono font-bold text-[#1B2559]">
                {config.format(value)}
              </div>
              <div className="text-xs text-[#8B8FA7] mt-0.5">
                {config.label}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
